import { SPOTLIGHT_PADDING, spotlightClassName } from '@/features/session-tutorial/sessionTutorial.styles';
import type { SessionTutorialSpotlightProps, SpotlightBox } from '@/features/session-tutorial/sessionTutorial.types';
import { SESSION_TUTORIAL_UI_ATTRIBUTE } from '@/features/session-tutorial/useInertOutside';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const sameBox = (a: SpotlightBox | null, b: SpotlightBox) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/** Follows the target every frame: session pages scroll, resize and animate under it. */
const useTargetBox = (target: HTMLElement) => {
  const [box, setBox] = useState<SpotlightBox | null>(null);

  useEffect(() => {
    target.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    let frame = 0;
    const measure = () => {
      const { top, left, width, height } = target.getBoundingClientRect();
      const next = { top, left, width, height };
      setBox((current) => (sameBox(current, next) ? current : next));
      frame = requestAnimationFrame(measure);
    };
    measure();
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return box;
};

/** Visual only: the page outside the target is made inert by the provider. */
export const SessionTutorialSpotlight = ({ target }: SessionTutorialSpotlightProps) => {
  const box = useTargetBox(target);
  if (!box) return null;

  return createPortal(
    <div
      aria-hidden
      {...{ [SESSION_TUTORIAL_UI_ATTRIBUTE]: '' }}
      data-testid="session-tutorial-spotlight"
      className={spotlightClassName}
      style={{
        top: box.top - SPOTLIGHT_PADDING,
        left: box.left - SPOTLIGHT_PADDING,
        width: box.width + SPOTLIGHT_PADDING * 2,
        height: box.height + SPOTLIGHT_PADDING * 2,
      }}
    />,
    document.body
  );
};
